import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCall } from "@/api/commerce";
import { useSamples, type Sample, type SampleStatus } from "@/api/ops";
import { Badge } from "@/components/crm/Badge";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Alert, Modal } from "@/components/ui";
import { shortDate } from "@/lib/format";

export const SAMPLE_STATUS: Record<SampleStatus, { label: string; tone: "neutral" | "brand" | "ok" | "danger" | "warn" }> = {
  requested: { label: "Requested", tone: "neutral" }, dispatched: { label: "Dispatched", tone: "brand" }, delivered: { label: "Delivered", tone: "brand" },
  on_trial: { label: "On trial", tone: "warn" }, approved: { label: "Approved", tone: "ok" }, rejected: { label: "Rejected", tone: "danger" },
};
const message = (e: unknown) => (e instanceof ApiError ? (e.problem.errors?.map((x) => `${x.field}: ${x.message}`).join("; ") || e.message) : "Could not save");
const today = () => new Date().toLocaleDateString("en-CA");

function NewSample({ dealId, onClose }: { dealId: string; onClose: () => void }) {
  const save = useCall<Sample>("samples", "timeline");
  const [kind, setKind] = useState<"sample" | "trial">("sample");
  const [product, setProduct] = useState<PickerValue | null>(null);
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [chargeable, setChargeable] = useState(false);
  const [due, setDue] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Send a sample or start a trial" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault(); setError(null);
        save.mutateAsync({ path: "/samples", body: { deal_id: dealId, kind, product_id: product?.id ?? null, description: description.trim() || null, quantity, chargeable, feedback_due: due || null } })
          .then(onClose).catch((err) => setError(message(err)));
      }}>
        {error && <Alert>{error}</Alert>}
        <div className="flex gap-4 text-sm">{(["sample", "trial"] as const).map((k) => <label key={k} className="flex items-center gap-1.5"><input type="radio" name="kind" className="accent-brand" checked={kind === k} onChange={() => setKind(k)} />{k === "sample" ? "Sample" : "Trial run"}</label>)}</div>
        <RecordPicker label="Product" endpoint="/products" value={product} onChange={setProduct} />
        <div><label htmlFor="smp-desc" className="label">Description</label><input id="smp-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Blank to use the product name" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label htmlFor="smp-qty" className="label">Quantity</label><input id="smp-qty" className="input" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
          <div><label htmlFor="smp-due" className="label">Feedback due</label><input id="smp-due" className="input" type="date" min={today()} value={due} onChange={(e) => setDue(e.target.value)} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-brand" checked={chargeable} onChange={(e) => setChargeable(e.target.checked)} />Chargeable</label>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={save.isPending}>Save</button></div>
      </form>
    </Modal>
  );
}

type Step = { to: SampleStatus; label: string };
const NEXT: Partial<Record<SampleStatus, Step[]>> = {
  requested: [{ to: "dispatched", label: "Dispatch" }],
  dispatched: [{ to: "delivered", label: "Delivered" }],
  delivered: [{ to: "on_trial", label: "On trial" }, { to: "approved", label: "Approved" }, { to: "rejected", label: "Rejected" }],
  on_trial: [{ to: "approved", label: "Approved" }, { to: "rejected", label: "Rejected" }],
};

function Advance({ sample, step, onClose }: { sample: Sample; step: Step; onClose: () => void }) {
  const save = useCall<Sample>("samples", "timeline");
  const [courier, setCourier] = useState(""); const [tracking, setTracking] = useState(""); const [on, setOn] = useState(today());
  const [feedback, setFeedback] = useState(""); const [due, setDue] = useState(sample.feedback_due ?? "");
  const [error, setError] = useState<string | null>(null);
  const decided = step.to === "approved" || step.to === "rejected";
  const body: Record<string, unknown> = { status: step.to };
  if (step.to === "dispatched") Object.assign(body, { courier: courier || null, tracking_number: tracking || null, dispatched_on: on });
  if (step.to === "on_trial" && due) body.feedback_due = due;
  if (decided) body.feedback = feedback.trim() || null;
  return (
    <Modal title={`${sample.description}: ${step.label.toLowerCase()}`} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setError(null); save.mutateAsync({ method: "PATCH", path: `/samples/${sample.id}`, body }).then(onClose).catch((err) => setError(message(err))); }}>
        {error && <Alert>{error}</Alert>}
        {step.to === "dispatched" && <div className="grid gap-4 sm:grid-cols-3">
          <div><label htmlFor="adv-on" className="label">Dispatched on</label><input id="adv-on" className="input" type="date" value={on} onChange={(e) => setOn(e.target.value)} /></div>
          <div><label htmlFor="adv-courier" className="label">Courier</label><input id="adv-courier" className="input" value={courier} onChange={(e) => setCourier(e.target.value)} /></div>
          <div><label htmlFor="adv-track" className="label">Tracking number</label><input id="adv-track" className="input" value={tracking} onChange={(e) => setTracking(e.target.value)} /></div>
        </div>}
        {step.to === "on_trial" && <div><label htmlFor="adv-due" className="label">Feedback due</label><input id="adv-due" className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>}
        {decided && <div><label htmlFor="adv-fb" className="label">Customer feedback</label><textarea id="adv-fb" className="input min-h-24" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="e.g. Passed fit and finish test; hardness 58 HRC" /></div>}
        {step.to === "delivered" && <p className="text-sm text-muted">Mark this {sample.kind} as received by the customer.</p>}
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={save.isPending}>Save</button></div>
      </form>
    </Modal>
  );
}

export function SampleRow({ s, canEdit, showDeal = false }: { s: Sample; canEdit: boolean; showDeal?: boolean }) {
  const [step, setStep] = useState<Step | null>(null);
  const st = SAMPLE_STATUS[s.status];
  return (
    <li className="px-5 py-3" aria-label={s.description}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{s.description}</span>
        <span className="text-sm text-muted">{Number(s.quantity)} {s.uom}{s.kind === "trial" ? " · trial" : ""}{s.chargeable ? " · chargeable" : ""}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
        {s.overdue && <Badge tone="danger">Feedback overdue</Badge>}
      </div>
      <p className="mt-0.5 text-xs text-muted">
        {showDeal && <><Link className="text-brand hover:underline" to={`/deals/${s.deal.id}`}>{s.deal.name}</Link>{s.account && ` · ${s.account.name}`} · </>}
        {s.dispatched_on && `Sent ${shortDate(s.dispatched_on)}${s.courier ? ` by ${s.courier}` : ""}${s.tracking_number ? ` (${s.tracking_number})` : ""} · `}
        {s.feedback_due && `Feedback due ${shortDate(s.feedback_due)}`}
      </p>
      {s.feedback && <p className="mt-1 text-sm">“{s.feedback}”</p>}
      {canEdit && NEXT[s.status] && <div className="mt-2 flex flex-wrap gap-2">{NEXT[s.status]!.map((n) => <button key={n.to} type="button" className="btn-ghost border border-line px-2 py-1 text-xs" onClick={() => setStep(n)}>{n.label}</button>)}</div>}
      {step && <Advance sample={s} step={step} onClose={() => setStep(null)} />}
    </li>
  );
}

export function SamplesPanel({ dealId, canEdit }: { dealId: string; canEdit: boolean }) {
  const samples = useSamples({ deal_id: dealId });
  const [adding, setAdding] = useState(false);
  return (
    <section className="card" aria-label="Samples and trials">
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <h2 className="font-semibold">Samples and trials</h2>
        {canEdit && <button type="button" className="btn-ghost text-xs" onClick={() => setAdding(true)}>Send sample</button>}
      </div>
      {samples.data?.length ? <ul className="divide-y divide-line">{samples.data.map((s) => <SampleRow key={s.id} s={s} canEdit={canEdit} />)}</ul>
        : <p className="px-5 py-4 text-sm text-muted">No samples sent for this deal.</p>}
      {adding && <NewSample dealId={dealId} onClose={() => setAdding(false)} />}
    </section>
  );
}
